import { useContext, useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { UserContext } from '~/contexts/UserContext';
import { PrepApi } from '~/api/prep';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/shadcn/components/ui/Table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/shadcn/components/ui/DropdownMenu';
import { Button } from '~/shadcn/components/ui/Button';
import { Badge } from '~/shadcn/components/ui/Badge';
import { MoreHorizontal, AlertCircle } from 'lucide-react';
import PrepActionModal from './PrepActionModal';
import { MovePalletAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/movePalletAction';
import { PrintPalletLabelAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/printPalletLabelAction';
import { PrintCaseLabelAction } from '~/components/warehouse/components/locationInventoryV2Components/actions/printCaseLabelAction';

const LocationBadge = ({ location, preppedDate }) => (
    <div>
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 rounded-md font-semibold">{location}</Badge>
        {preppedDate && <div className="text-muted-foreground text-xs">Prepped: {preppedDate.split(' ')?.[0]}</div>}
    </div>
);

function PrepCompleteTable({ locationFilter }) {
    const { user } = useContext(UserContext);
    const prepApi = new PrepApi();

    const [completeData, setCompleteData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [selectedRowData, setSelectedRowData] = useState(null);

    const fetchCompletePallets = useCallback(async () => {
        if (!user?.facilityID) return;

        try {
            setIsLoading(true);
            const response = await prepApi.getPalletsInPrepLocations({
                facilityId: user.facilityID,
                type: 'Complete',
                locationName: locationFilter,
            });
            if (response.status) {
                setCompleteData(response.data.rows);
            } else {
                setError('Failed to fetch complete pallets.');
            }
        } catch (err) {
            setError(err.message || 'An unexpected error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, [user?.facilityID, locationFilter]);

    const handleActionClick = (action, rowData) => {
        setSelectedAction(action);
        setSelectedRowData(rowData);
        setIsModalOpen(true);
    };

    const handleSuccess = () => {
        setIsModalOpen(false);
        fetchCompletePallets();
        toast.success('Action completed successfully!');
    };

    const renderModalContent = () => {
        if (!selectedAction) return null;

        switch (selectedAction) {
            case 'Print Pallet Label':
                return <PrintPalletLabelAction palletId={selectedRowData?.palletId} onSuccess={handleSuccess} />;
            case 'Print RPC Labels':
                return <PrintCaseLabelAction lotId={selectedRowData?.lotId} onSuccess={handleSuccess} />;
            case 'Move Pallet':
                return (
                    <MovePalletAction
                        palletId={selectedRowData?.palletId}
                        onSuccess={handleSuccess}
                        inNonMuiModal={true}
                    />
                );
            default:
                return null;
        }
    };

    useEffect(() => {
        fetchCompletePallets();
    }, [fetchCompletePallets]);

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center text-center py-10">
                <AlertCircle className="h-10 w-10 text-red-500 mb-3" />
                <p className="text-lg font-semibold text-red-600">Failed to load data</p>
                <p className="text-muted-foreground">{error}</p>
            </div>
        );
    }

    if (!isLoading && completeData.length === 0) {
        return (
            <div className="text-center py-10">
                <p className="text-lg font-semibold">No Prep Complete Pallets Found</p>
                <p className="text-muted-foreground">
                    There are no pallets in prep complete locations{locationFilter ? ` for ${locationFilter}` : ''}.
                </p>
            </div>
        );
    }
    return (
        <>
            <Table>
                <TableHeader>
                    <TableRow className="hover:bg-transparent">
                        <TableHead>PALLET</TableHead>
                        <TableHead>LOT</TableHead>
                        <TableHead>ITEM</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>LOCATION</TableHead>
                        <TableHead>EXPIRATION</TableHead>
                        <TableHead>RPCS</TableHead>
                        <TableHead>UNITS PER RPC</TableHead>
                        <TableHead>LOOSE UNITS</TableHead>
                        <TableHead>TOTAL UNITS</TableHead>
                        <TableHead>ACTIONS</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {completeData.map((row) => (
                        <TableRow key={row.palletId}>
                            <TableCell className="font-medium">{row.palletId}</TableCell>
                            <TableCell className="font-medium">{row.lotId}</TableCell>
                            <TableCell>{row.itemName}</TableCell>
                            <TableCell className="font-medium">{row.sku}</TableCell>
                            <TableCell>
                                <LocationBadge location={row.locationName} preppedDate={row?.datePrepped} />
                            </TableCell>
                            <TableCell className="font-medium">{row.expirationDate}</TableCell>
                            <TableCell className="font-medium">{row.numCases}</TableCell>
                            <TableCell className="font-medium">{row.unitsPerCase}</TableCell>
                            <TableCell className="font-medium">{row.numLooseUnits}</TableCell>
                            <TableCell className="font-medium">{row.totalUnits}</TableCell>
                            <TableCell>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                            <span className="sr-only">Open menu</span>
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => handleActionClick('Print Pallet Label', row)}>
                                            Print Pallet Label
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleActionClick('Print RPC Labels', row)}>
                                            Print RPC Labels
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleActionClick('Move Pallet', row)}>
                                            Move Pallet
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <PrepActionModal isOpen={isModalOpen} onOpenChange={setIsModalOpen} title={selectedAction || ''}>
                {renderModalContent()}
            </PrepActionModal>
        </>
    );
}

export default PrepCompleteTable;
